import React from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";

import { GlobalLocationState } from "../../../../commons/types/location";
import * as icons from "../../../../resources/icons/";
import AnnotatedButton from "../../components/AnnotatedButton/AnnotatedButton";
import Button from "../../components/Button/Button";
import CenteredContent from "../../components/CenteredContent/CenteredContent";
import Headline from "../../components/Headline/Headline";
import Icon from "../../components/Icon/Icon";
import InputField from "../../components/InputField/InputField";
import SingleInputFormLayout from "../../components/SingleInputFormLayout/SingleInputFormLayout";
import config from "../../config";
import { useAppHistory, useAppLocation } from "../../libs/hooks/use-app-routing";
import useDebounce from "../../libs/hooks/use-debounce";
import useEnv from "../../libs/queries/use-env";
import useProductFilter from "../../libs/queries/use-product-filter";
import useProducts from "../../libs/queries/use-products";
import { ROUTES } from "../../routes";

interface Props extends RouteComponentProps<EmptyObject, EmptyObject, GlobalLocationState> {
  productFilter: ReturnType<typeof useProductFilter>;
}

const SearchContent = ({ productFilter }: Props) => {
  const location = useAppLocation();
  const history = useAppHistory();
  const { t } = useTranslation(["commons"]);

  const env = useEnv();

  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, config.shared.filter.filterFetchDelay);

  const nextActiveFilters = productFilter.activeFilters;
  const products = useProducts({
    productType: env.productType,
    activeFilters: nextActiveFilters,
    searchTerm: debouncedSearchTerm,
    limit: 0
  });

  const productsCount = products.query.data?.totalFiltered ?? 0;

  const bikeLabel = productFilter.showsOnlyEBikes
    ? t("commons:searchContentProductFinderOverview.actionLabel.resultsEBikes", {
        count: productsCount
      })
    : t("commons:searchContentProductFinderOverview.actionLabel.resultsBikes", {
        count: productsCount
      });

  const submitButtonLabel = products.query.isFetching
    ? t("commons:searchContentProductFinderOverview.actionLabel.loading")
    : bikeLabel;

  return (
    <CenteredContent classNames={["u-padding-bottom-xxxxl"]}>
      <Headline classNames={["u-space-l"]}>{t("commons:searchContentProductFinderOverview.searchHeadline")}</Headline>
      <SingleInputFormLayout
        input={
          <InputField
            type="search"
            placeholder={t("commons:searchContentProductFinderOverview.searchInputPlaceholder")}
            value={searchTerm}
            onChange={setSearchTerm}
            icon={<Icon source={icons.IconSmallSearch} />}
          />
        }
        sideComponents={
          <AnnotatedButton
            inline
            annotation={t("commons:searchContentProductFinderOverview.activeFiltersLabel", {
              count: productFilter.activeFiltersCount
            })}
          >
            <Button
              icon={<Icon source={icons.IconSmallArrowRight} />}
              iconRight
              variant="accent"
              kind="solid"
              disabled={products.query.isFetching || products.query.data?.totalFiltered === 0}
              onClick={() => {
                productFilter.setActiveFilters(nextActiveFilters);
                history.push(ROUTES.PRODUCT_FINDER.INDEX, {
                  ...(location.state ?? {}),
                  // If `goBackToDetails` is set, we know the user visited details content page before
                  // We reset that state to prevent the user from being redirected to details page after submitting the searchTerm
                  goBackToDetails: undefined,
                  searchTerm
                });
              }}
            >
              {submitButtonLabel}
            </Button>
          </AnnotatedButton>
        }
      />
    </CenteredContent>
  );
};

export default SearchContent;
